import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
    Event as RouterEvent,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    Router,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { PersonalizationService } from '../../../app/services/personalization.service';

/**
 * Failsafe: hide the splash no later than this even if the router never emits a
 * terminal navigation event (e.g. an exception thrown during activation), or if
 * branding resolution (see markBrandingResolved()) never fires. Prevents the
 * overlay from being stuck forever while the app is already rendered behind it.
 */
const SPLASH_FAILSAFE_MS = 10000;

@Injectable()
export class FuseSplashScreenService
{
    private _navigationEnded = false;
    private _brandingResolved = false;
    private _hidden = false;

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: any,
        private _router: Router,
        private _personalization: PersonalizationService,
    )
    {
        // Hide on the FIRST settled navigation — not only NavigationEnd. A guard
        // redirect (UrlTree) settles as NavigationCancel and an activation failure as
        // NavigationError; if we waited for NavigationEnd alone the splash would stay
        // up forever in those cases even though the app has already rendered.
        //
        // The splash is only actually hidden once navigation has settled AND the
        // correct NWC/WhiteHelmet branding has been applied (see
        // markBrandingResolved()), so the app is never revealed while showing the
        // wrong logo (e.g. on a user's very first login).
        this._router.events
            .pipe(
                filter(
                    (event: RouterEvent) =>
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel ||
                        event instanceof NavigationError,
                ),
                take(1),
            )
            .subscribe(() => {
                this._navigationEnded = true;
                this._tryHide();
            });

        // Hard failsafe in case no terminal navigation event ever arrives, or
        // branding resolution (see markBrandingResolved()) never fires.
        setTimeout(() => {
            this._navigationEnded = true;
            this._brandingResolved = true;
            this.hide();
        }, SPLASH_FAILSAFE_MS);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the splash screen
     */
    show(): void
    {
        this._hidden = false;
        this._document.body.classList.remove('fuse-splash-screen-hidden');
    }

    /**
     * Hide the splash screen
     */
    hide(): void
    {
        if (this._hidden) {
            return;
        }
        this._hidden = true;
        this._document.body.classList.add('fuse-splash-screen-hidden');
    }

    /**
     * Signals that the correct NWC/WhiteHelmet branding has already been
     * applied to the splash screen (see AppComponent.ngOnInit), so it's now
     * safe to reveal the app without risking a flash of the wrong logo.
     */
    markBrandingResolved(): void
    {
        this._brandingResolved = true;
        this._tryHide();
    }

    private _tryHide(): void
    {
        if (!this._navigationEnded || !this._brandingResolved)
        {
            return;
        }

        this.hide();
    }
}
